<template>
  <div class="kpi-analysis">
    <div class="header">
      <div class="left" @click="handleHeaderBack">
        <van-icon name="arrow-left" size="22" color="#0f172a" />
      </div>
      <div class="title">
        {{ activeDrillDown ? $t(activeDrillDown.titleKey) : $t('kpi_analysis_title') }}
      </div>
      <div class="right"></div>
    </div>

    <div v-if="!activeDrillDown" class="content">
      <div v-if="dashboardLoading" class="kpi-loading-state">
        <div class="kpi-loading-indicator">
          <van-loading color="#aa341b" size="22px" />
        </div>

        <div class="skeleton-summary-card">
          <div class="skeleton-line skeleton-title-line"></div>
          <div class="skeleton-line skeleton-desc-line"></div>
          <div class="skeleton-line skeleton-desc-line short"></div>
        </div>

        <div
          v-for="sectionIndex in 3"
          :key="sectionIndex"
          class="skeleton-section-card"
        >
          <div class="skeleton-section-header">
            <div class="skeleton-badge"></div>
            <div class="skeleton-line skeleton-section-title"></div>
          </div>
          <div class="skeleton-metrics">
            <div
              v-for="metricIndex in 4"
              :key="metricIndex"
              class="skeleton-metric-card"
            >
              <div class="skeleton-metric-header">
                <div class="skeleton-line skeleton-metric-title"></div>
                <div class="skeleton-pill"></div>
              </div>
              <div class="skeleton-line skeleton-value-line"></div>
              <div class="skeleton-line skeleton-foot-line"></div>
              <div class="skeleton-track"></div>
            </div>
          </div>
        </div>
      </div>

      <template v-else>
        <div class="summary-card">
          <div class="summary-title-row">
            <span class="summary-title">KPI for {{ roleLabel }}</span>
            <span class="updated-at">{{ $t('statistics_as_of') }} {{ dashboard.updatedAt }}</span>
          </div>
          <div class="summary-desc">{{ $t('kpi_summary_desc') }}</div>
        </div>

        <div
          v-for="(section, index) in dashboard.sections"
          :key="section.id"
          class="section-card"
        >
          <div class="section-header">
            <div class="section-title-wrap">
              <span class="section-badge" :style="{ background: section.softColor, color: section.accent }">{{ index + 1 }}</span>
              <span class="section-title">{{ $t(section.titleKey) }}</span>
            </div>
          </div>

          <div class="section-metrics" :class="metricGridClass(section)">
            <div
              v-for="metric in section.metrics"
              :key="metric.titleKey + metric.type"
              class="metric-card"
              :class="[
                'metric-' + metric.type,
                { 'metric-drillable': isMetricDrillable(section, metric) }
              ]"
              :style="sectionStyle(section)"
              :role="isMetricDrillable(section, metric) ? 'button' : null"
              :tabindex="isMetricDrillable(section, metric) ? 0 : null"
              @click="openMetricDrillDown(section, metric)"
              @keyup.enter="openMetricDrillDown(section, metric)"
            >
              <div class="metric-header">
                <div class="metric-title">{{ $t(metric.titleKey) }}</div>
                <div class="metric-header-actions">
                  <div v-if="metric.periodKey" class="metric-period">{{ $t(metric.periodKey) }}</div>
                  <van-icon
                    v-if="isMetricDrillable(section, metric)"
                    name="arrow"
                    class="metric-drill-arrow"
                    size="12"
                  />
                </div>
              </div>

              <template v-if="metric.type === 'coverage'">
                <div class="coverage-list">
                  <div v-for="item in metric.coverage" :key="item.name" class="coverage-row">
                    <div class="coverage-value-item">
                      <span class="coverage-name">{{ item.name }}</span>
                      <span class="coverage-value">{{ item.value }}</span>
                      <span v-if="item.targetLabel" class="coverage-target">{{ item.targetLabel }}</span>
                    </div>
                    <div class="coverage-track">
                      <div class="coverage-fill" :style="{ width: getProgressWidth(item.progress), background: coverageColor(item.progress, section.accent, item.target) }"></div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="metric-value-row">
                  <span class="metric-value">{{ metric.value }}</span>
                  <span v-if="metric.target" class="metric-target">/ {{ metric.target }}</span>
                  <span v-if="metric.unitKey" class="metric-unit">{{ $t(metric.unitKey) }}</span>
                </div>

                <div v-if="metric.type === 'threshold'" class="threshold-status">
                  <van-icon name="checked" size="14" :color="section.accent" />
                  <span>{{ $t('kpi_threshold_reached') }}</span>
                </div>

                <template v-else>
                  <div class="metric-footer">{{ $t(metric.footerKey) }} {{ metric.footerValue }}</div>
                  <div class="progress-track">
                    <div class="progress-fill" :style="{ width: getProgressWidth(metric.progress), background: section.accent }"></div>
                  </div>
                </template>
              </template>
            </div>
          </div>
        </div>

        <button
          v-if="dashboard.sections.length"
          type="button"
          class="kpi-copilot-entry"
          @click="openKpiCopilot"
        >
          <span class="kpi-copilot-title">{{ $t('kpi_more_suggestions_cta') }}</span>
          <van-icon name="arrow" class="kpi-copilot-arrow" size="14" />
        </button>
        <div v-if="dashboard.sections.length" class="kpi-copilot-desc">
          {{ $t('kpi_more_suggestions_desc') }}
        </div>
      </template>
    </div>

    <div v-else class="drill-down-content">
      <KPIDrillDown
        :key="activeDrillDown.titleKey"
        :groups="activeDrillDownGroups"
        :mode="activeDrillDown.mode"
        @create-visit="createVisit"
      />
    </div>
  </div>
</template>

<script>
import { Icon, Loading } from 'vant';
import { getLocalStorage, isGBA } from '@/utils/index';
import { newGetPhysicianList, fetchPhysicianMrCallsByDateRange } from '@/api/Physicians';
import { getContentSendOpenStats } from '@/api/content';
import { getKpiDashboard } from './kpiConfig';
import KPIDrillDown from './KPIDrillDown.vue';

const CONTENT_PUSH_TARGET = 150;
const TARGET_CUSTOMER_BIND_RATE_TARGET = 60;
const AB_RATIO_TARGET = 50;
const TARGET_CUSTOMER_TARGET = 150;
const WEEKLY_VISIT_PLAN_TARGET = 15;
const DAILY_FACE_TO_FACE_TARGET = 10;
const FACE_TO_FACE_CALL_CHANNEL = '面对面拜访';
const ABC_COVERAGE_TARGETS = {
  A: 90,
  B: 90,
  C: 40
};
const ABC_MONTHLY_FREQUENCY_TARGETS = {
  A: 4,
  B: 2,
  C: 1
};
const MAX_TARGET_CUSTOMER_PAGE_SIZE = 500;

export default {
  name: 'KPIAnalysis',
  components: {
    [Icon.name]: Icon,
    [Loading.name]: Loading,
    KPIDrillDown
  },
  data() {
    return {
      dashboardLoading: true,
      enableDrillDown: false,
      activeDrillDown: null,
      customerDrillDownGroups: {
        A: [],
        B: [],
        C: []
      },
      digitalBindCustomers: [],
      roleLabel: 'MR',
      userId: getLocalStorage('user_id') || '',
      roleId: getLocalStorage('role_id') || '',
      dashboard: {
        year: '2026',
        period: '',
        updatedAt: '',
        sections: []
      }
    };
  },
  computed: {
    activeDrillDownGroups() {
      if (!this.activeDrillDown) return {};
      if (this.activeDrillDown.mode === 'binding') {
        return {
          all: this.digitalBindCustomers
        };
      }
      return this.customerDrillDownGroups;
    }
  },
  created() {
    const roleName = getLocalStorage('role_name');
    this.enableDrillDown = roleName === '销售' || roleName === 'MR';
    this.roleLabel = roleName === '销售' ? 'MR' : (roleName || 'MR');
    this.dashboard = getKpiDashboard(roleName);
    this.dashboard.year = `${new Date().getFullYear()}`;
    this.dashboard.period = this.getCurrentQuarterLabel();
    this.dashboard.updatedAt = this.buildStatisticsAsOf();
    this.loadDashboardData();
  },
  methods: {
    buildStatisticsAsOf() {
      const date = new Date();
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const day = `${date.getDate()}`.padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
    getCurrentQuarter() {
      const date = new Date();
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${date.getFullYear()}Q${quarter}`;
    },
    getCurrentQuarterLabel() {
      const date = new Date();
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${date.getFullYear()} Q${quarter}`;
    },
    getCurrentMonthRange() {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return {
        start: start.toISOString(),
        end: end.toISOString()
      };
    },
    getCurrentWeekRange() {
      const now = new Date();
      const day = now.getDay() || 7;
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day + 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 7);

      return {
        start: start.toISOString(),
        end: end.toISOString()
      };
    },
    isWorkday(date) {
      const day = date.getDay();
      return day !== 0 && day !== 6;
    },
    getMonthToDateWorkdays() {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const endTime = now.getTime();
      let workdays = 0;

      for (let cursorTime = start.getTime(); cursorTime <= endTime; cursorTime += 24 * 60 * 60 * 1000) {
        const cursor = new Date(cursorTime);
        if (this.isWorkday(cursor)) {
          workdays++;
        }
      }

      return workdays;
    },
    findMetric(sectionId, titleKey) {
      const section = this.dashboard.sections.find(item => item.id === sectionId);
      if (!section) return null;
      return section.metrics.find(item => item.titleKey === titleKey) || null;
    },
    setMetric(sectionId, titleKey, values) {
      const metric = this.findMetric(sectionId, titleKey);
      if (!metric) return;

      Object.keys(values).forEach((key) => {
        this.$set(metric, key, values[key]);
      });
    },
    getCount(res) {
      if (!res) return 0;
      if (typeof res.data_count === 'number') return res.data_count;
      if (typeof res['@odata.count'] === 'number') return res['@odata.count'];
      if (Array.isArray(res.data)) return res.data.length;
      if (Array.isArray(res.value)) return res.value.length;
      return 0;
    },
    getPercentValue(count, total) {
      if (!total) return 0;
      return Math.round((count / total) * 100);
    },
    getProgressValue(value, target) {
      if (!target) return 0;
      return Math.min(Math.round((value / target) * 100), 100);
    },
    getPhysicianId(item) {
      return item && (item.physician_id || (item.physician && item.physician.physician_id));
    },
    getMonthlyCallCount(item, callCounts) {
      const physicianId = this.getPhysicianId(item);
      return physicianId ? (callCounts.get(String(physicianId)) || 0) : 0;
    },
    isWechatBound(item) {
      return item.is_wechat === true ||
        ['1', 'true'].includes(String(item.is_wechat || '').toLowerCase());
    },
    buildCustomerDrillDownItem(item, customerLevel, callCounts) {
      const monthlyCallCount = this.getMonthlyCallCount(item, callCounts);

      return {
        ...item,
        physician_id: this.getPhysicianId(item),
        customerLevel,
        monthlyCallCount,
        requiredCallCount: ABC_MONTHLY_FREQUENCY_TARGETS[customerLevel],
        isCovered: monthlyCallCount > 0
      };
    },
    buildPhysicianCountParams(extraParams = {}) {
      return {
        user_id: this.userId,
        page_index: 1,
        page_size: 1,
        ...extraParams
      };
    },
    fetchPhysicianCount(extraParams = {}) {
      return newGetPhysicianList(this.buildPhysicianCountParams(extraParams), 'physician_name')
        .then(res => this.getCount(res))
        .catch((e) => {
          console.error('Fetch KPI physician count failed', e);
          return 0;
        });
    },
    fetchTargetPhysicians(extraParams = {}) {
      return newGetPhysicianList({
        ...this.buildPhysicianCountParams(extraParams),
        page_size: MAX_TARGET_CUSTOMER_PAGE_SIZE
      }, 'physician_name')
        .then(res => (res && Array.isArray(res.data)) ? res.data : [])
        .catch((e) => {
          console.error('Fetch KPI target physicians failed', e);
          return [];
        });
    },
    async fetchCurrentMonthCallCounts() {
      try {
        const { start, end } = this.getCurrentMonthRange();
        const res = await fetchPhysicianMrCallsByDateRange({
          is_co_call: 0,
          call_user_id: this.userId,
          role_id: this.roleId
        }, start, end, 0, 1000);

        const callCounts = new Map();
        ((res && Array.isArray(res.value)) ? res.value : [])
          .map(item => this.getPhysicianId(item))
          .filter(Boolean)
          .forEach((physicianId) => {
            const normalizedId = String(physicianId);
            callCounts.set(normalizedId, (callCounts.get(normalizedId) || 0) + 1);
          });

        return callCounts;
      } catch (e) {
        console.error('Fetch KPI monthly call counts failed', e);
        return new Map();
      }
    },
    async fetchCurrentWeekPlannedPhysicianIds() {
      try {
        const { start, end } = this.getCurrentWeekRange();
        const res = await fetchPhysicianMrCallsByDateRange({
          is_co_call: 0,
          call_user_id: this.userId,
          role_id: this.roleId,
          crm_status: 'Planned_vod'
        }, start, end, 0, 1000);

        return new Set(((res && Array.isArray(res.value)) ? res.value : [])
          .map(item => item.physician_id || (item.physician && item.physician.physician_id))
          .filter(Boolean));
      } catch (e) {
        console.error('Fetch KPI planned visits failed', e);
        return new Set();
      }
    },
    async fetchCurrentMonthFaceToFaceCallCount() {
      try {
        const { start, end } = this.getCurrentMonthRange();
        const res = await fetchPhysicianMrCallsByDateRange({
          is_co_call: 0,
          call_user_id: this.userId,
          role_id: this.roleId,
          crm_status: 'Submitted_vod',
          call_channel: FACE_TO_FACE_CALL_CHANNEL
        }, start, end, 0, 1000);

        return this.getCount(res);
      } catch (e) {
        console.error('Fetch KPI face-to-face calls failed', e);
        return 0;
      }
    },
    countVisitedTargets(targetList, callCounts) {
      return targetList
        .filter(item => this.getMonthlyCallCount(item, callCounts) > 0)
        .length;
    },
    countFrequencyQualifiedTargets(targetList, callCounts, targetFrequency) {
      return targetList
        .filter(item => this.getMonthlyCallCount(item, callCounts) >= targetFrequency)
        .length;
    },
    getFrequencyTargetLabel(targetFrequency) {
      return /^en/i.test(this.$i18n && this.$i18n.locale ? this.$i18n.locale : '')
        ? `>=${targetFrequency} calls/customer`
        : `>=${targetFrequency}次/客户`;
    },
    async loadCustomerMetrics() {
      const [
        targetCount,
        abTargetCount,
        aTargetList,
        bTargetList,
        cTargetList,
        monthlyCallCounts
      ] = await Promise.all([
        this.fetchPhysicianCount({ is_target: 'true' }),
        this.fetchPhysicianCount({ is_target: 'true', hcp_classes: ['A', 'B'] }),
        this.fetchTargetPhysicians({ is_target: 'true', hcp_classes: 'A' }),
        this.fetchTargetPhysicians({ is_target: 'true', hcp_classes: 'B' }),
        this.fetchTargetPhysicians({ is_target: 'true', hcp_classes: 'C' }),
        this.fetchCurrentMonthCallCounts()
      ]);

      const aTargetCount = aTargetList.length;
      const bTargetCount = bTargetList.length;
      const cTargetCount = cTargetList.length;
      const aCoveredCount = this.countVisitedTargets(aTargetList, monthlyCallCounts);
      const bCoveredCount = this.countVisitedTargets(bTargetList, monthlyCallCounts);
      const cCoveredCount = this.countVisitedTargets(cTargetList, monthlyCallCounts);
      const aFrequencyCount = this.countFrequencyQualifiedTargets(aTargetList, monthlyCallCounts, ABC_MONTHLY_FREQUENCY_TARGETS.A);
      const bFrequencyCount = this.countFrequencyQualifiedTargets(bTargetList, monthlyCallCounts, ABC_MONTHLY_FREQUENCY_TARGETS.B);
      const cFrequencyCount = this.countFrequencyQualifiedTargets(cTargetList, monthlyCallCounts, ABC_MONTHLY_FREQUENCY_TARGETS.C);
      const abRatio = this.getPercentValue(abTargetCount, targetCount);
      const aCoverage = this.getPercentValue(aCoveredCount, aTargetCount);
      const bCoverage = this.getPercentValue(bCoveredCount, bTargetCount);
      const cCoverage = this.getPercentValue(cCoveredCount, cTargetCount);
      const aFrequencyRate = this.getPercentValue(aFrequencyCount, aTargetCount);
      const bFrequencyRate = this.getPercentValue(bFrequencyCount, bTargetCount);
      const cFrequencyRate = this.getPercentValue(cFrequencyCount, cTargetCount);

      this.customerDrillDownGroups = {
        A: aTargetList.map(item => this.buildCustomerDrillDownItem(item, 'A', monthlyCallCounts)),
        B: bTargetList.map(item => this.buildCustomerDrillDownItem(item, 'B', monthlyCallCounts)),
        C: cTargetList.map(item => this.buildCustomerDrillDownItem(item, 'C', monthlyCallCounts))
      };

      this.setMetric('customer', 'kpi_target_customer_count', {
        value: `${targetCount}`,
        target: `${TARGET_CUSTOMER_TARGET}`,
        progress: this.getProgressValue(targetCount, TARGET_CUSTOMER_TARGET),
        footerValue: `${this.getProgressValue(targetCount, TARGET_CUSTOMER_TARGET)}%`
      });
      this.setMetric('customer', 'kpi_ab_customer_ratio', {
        value: `${abRatio}%`,
        progress: this.getProgressValue(abRatio, AB_RATIO_TARGET),
        footerValue: `>=${AB_RATIO_TARGET}%`
      });

      const coverageMetric = this.findMetric('customer', 'kpi_abc_customer_coverage');
      if (coverageMetric) {
        this.$set(coverageMetric, 'coverage', [
          {
            name: 'A',
            value: aTargetCount ? `${aCoverage}%` : '--',
            progress: aCoverage,
            achievement: this.getProgressValue(aCoverage, ABC_COVERAGE_TARGETS.A),
            target: ABC_COVERAGE_TARGETS.A,
            targetLabel: `>=${ABC_COVERAGE_TARGETS.A}%`
          },
          {
            name: 'B',
            value: bTargetCount ? `${bCoverage}%` : '--',
            progress: bCoverage,
            achievement: this.getProgressValue(bCoverage, ABC_COVERAGE_TARGETS.B),
            target: ABC_COVERAGE_TARGETS.B,
            targetLabel: `>=${ABC_COVERAGE_TARGETS.B}%`
          },
          {
            name: 'C',
            value: cTargetCount ? `${cCoverage}%` : '--',
            progress: cCoverage,
            achievement: this.getProgressValue(cCoverage, ABC_COVERAGE_TARGETS.C),
            target: ABC_COVERAGE_TARGETS.C,
            targetLabel: `>=${ABC_COVERAGE_TARGETS.C}%`
          }
        ]);
      }

      const frequencyMetric = this.findMetric('customer', 'kpi_abc_customer_monthly_coverage_frequency');
      if (frequencyMetric) {
        this.$set(frequencyMetric, 'coverage', [
          {
            name: 'A',
            value: aTargetCount ? `${aFrequencyCount}/${aTargetCount}` : '--',
            progress: aFrequencyRate,
            achievement: aFrequencyRate,
            target: 100,
            targetLabel: this.getFrequencyTargetLabel(ABC_MONTHLY_FREQUENCY_TARGETS.A)
          },
          {
            name: 'B',
            value: bTargetCount ? `${bFrequencyCount}/${bTargetCount}` : '--',
            progress: bFrequencyRate,
            achievement: bFrequencyRate,
            target: 100,
            targetLabel: this.getFrequencyTargetLabel(ABC_MONTHLY_FREQUENCY_TARGETS.B)
          },
          {
            name: 'C',
            value: cTargetCount ? `${cFrequencyCount}/${cTargetCount}` : '--',
            progress: cFrequencyRate,
            achievement: cFrequencyRate,
            target: 100,
            targetLabel: this.getFrequencyTargetLabel(ABC_MONTHLY_FREQUENCY_TARGETS.C)
          }
        ]);
      }
    },
    async loadDigitalMetrics() {
      try {
        const [res, targetCount, boundTargetCount, targetList] = await Promise.all([
          getContentSendOpenStats({
            quarter: this.getCurrentQuarter(),
            user_id: this.userId
          }),
          this.fetchPhysicianCount({ is_target: 'true' }),
          this.fetchPhysicianCount({ is_target: 'true', is_wechat: 'true' }),
          this.enableDrillDown
            ? this.fetchTargetPhysicians({ is_target: 'true' })
            : Promise.resolve([])
        ]);

        const stats = res && res.data ? res.data : {};
        const sendCount = Number(stats.send_count) || 0;
        const target = Number(stats.target_send_count) || CONTENT_PUSH_TARGET;
        const bindRate = this.getPercentValue(boundTargetCount, targetCount);

        this.digitalBindCustomers = targetList.map(item => ({
          ...item,
          physician_id: this.getPhysicianId(item),
          isBound: this.isWechatBound(item)
        }));

        this.setMetric('digital', 'kpi_target_customer_bind_rate', {
          value: `${bindRate}%`,
          progress: this.getProgressValue(bindRate, TARGET_CUSTOMER_BIND_RATE_TARGET),
          footerValue: `>=${TARGET_CUSTOMER_BIND_RATE_TARGET}%`
        });

        this.setMetric('digital', 'kpi_quarterly_content_push', {
          value: `${sendCount}`,
          target: `${target}`,
          progress: this.getProgressValue(sendCount, target),
          footerValue: `>=${target}`
        });
      } catch (e) {
        console.error('Fetch KPI content send stats failed', e);
      }
    },
    async loadVisitPlanMetrics() {
      const plannedPhysicianIds = await this.fetchCurrentWeekPlannedPhysicianIds();
      const plannedCount = plannedPhysicianIds.size;

      this.setMetric('behavior', 'kpi_visit_plan_count', {
        value: `${plannedCount}`,
        target: `${WEEKLY_VISIT_PLAN_TARGET}`,
        progress: this.getProgressValue(plannedCount, WEEKLY_VISIT_PLAN_TARGET),
        footerKey: 'kpi_weekly_target_short',
        footerValue: `>=${WEEKLY_VISIT_PLAN_TARGET}`
      });
    },
    async loadFaceToFaceCallMetrics() {
      const faceToFaceCount = await this.fetchCurrentMonthFaceToFaceCallCount();
      const workdays = this.getMonthToDateWorkdays();
      const dailyAverage = workdays ? faceToFaceCount / workdays : 0;
      const displayValue = Number.isInteger(dailyAverage)
        ? `${dailyAverage}`
        : dailyAverage.toFixed(1);

      this.setMetric('behavior', 'kpi_daily_face_to_face_calls', {
        value: displayValue,
        target: `${DAILY_FACE_TO_FACE_TARGET}`,
        progress: this.getProgressValue(dailyAverage, DAILY_FACE_TO_FACE_TARGET),
        footerValue: `>=${DAILY_FACE_TO_FACE_TARGET}`
      });
    },
    async loadDashboardData() {
      if (!this.userId) {
        this.dashboardLoading = false;
        return;
      }

      this.dashboardLoading = true;
      try {
        await Promise.all([
          this.loadCustomerMetrics(),
          this.loadDigitalMetrics(),
          this.loadVisitPlanMetrics(),
          this.loadFaceToFaceCallMetrics()
        ]);
      } finally {
        this.dashboardLoading = false;
      }
    },
    metricGridClass(section) {
      return `metrics-${Math.min(section.metrics.length, 4)}`;
    },
    isMetricDrillable(section, metric) {
      if (!this.enableDrillDown) return false;
      if (section.id === 'customer') {
        return metric.titleKey !== 'kpi_ab_customer_ratio';
      }
      return section.id === 'digital' && metric.titleKey === 'kpi_target_customer_bind_rate';
    },
    openMetricDrillDown(section, metric) {
      if (!this.isMetricDrillable(section, metric)) return;

      let mode = 'coverage';
      if (metric.titleKey === 'kpi_abc_customer_monthly_coverage_frequency') {
        mode = 'frequency';
      } else if (metric.titleKey === 'kpi_target_customer_bind_rate') {
        mode = 'binding';
      }

      this.activeDrillDown = {
        titleKey: metric.titleKey,
        mode
      };
    },
    handleHeaderBack() {
      if (this.activeDrillDown) {
        this.activeDrillDown = null;
        return;
      }
      this.$emit('close');
    },
    createVisit(customer) {
      this.$router.push({
        path: isGBA() ? '/gba/visit-create' : '/hcp/visit-create',
        query: {
          physician_id: customer.physician_id
        }
      });
    },
    sectionStyle(section) {
      return {
        '--section-accent': section.accent,
        '--section-soft': section.softColor
      };
    },
    getProgressWidth(progress) {
      const progressValue = Math.min(Number(progress) || 0, 100);
      const value = progressValue > 0 ? Math.max(10, progressValue) : 0;
      return `${value}%`;
    },
    coverageColor(progress, defaultColor, target) {
      if (!target) {
        if (progress >= 80) return defaultColor;
        if (progress >= 60) return '#f59e0b';
        return '#fb923c';
      }

      if (progress >= target) return defaultColor;
      if (progress >= target * 0.8) return '#f59e0b';
      return '#fb923c';
    },
    getMetricPeriodLabel(metric) {
      return metric.periodKey ? this.$t(metric.periodKey) : '';
    },
    buildProgressLabel(progress) {
      if (progress === undefined || progress === null || progress === '') return '';
      return `${Math.round(Number(progress) || 0)}%`;
    },
    getKpiPromptLabels() {
      const isEnglish = /^en/i.test(this.$i18n && this.$i18n.locale ? this.$i18n.locale : '');

      return isEnglish
        ? {
          value: 'value',
          target: 'target',
          unit: 'unit',
          progress: 'progress'
        }
        : {
          value: '当前值',
          target: '目标',
          unit: '单位',
          progress: '进度'
        };
    },
    buildKpiResultLines() {
      const lines = [];
      const labels = this.getKpiPromptLabels();

      this.dashboard.sections.forEach((section) => {
        const sectionTitle = this.$t(section.titleKey);

        section.metrics.forEach((metric) => {
          const metricTitle = this.$t(metric.titleKey);
          const periodLabel = this.getMetricPeriodLabel(metric);
          const title = periodLabel
            ? `[${sectionTitle}][${periodLabel}] ${metricTitle}`
            : `[${sectionTitle}] ${metricTitle}`;

          if (metric.type === 'coverage') {
            const coverageText = (metric.coverage || [])
              .map((item) => {
                const targetText = item.targetLabel ? `, ${labels.target} ${item.targetLabel}` : '';
                const progressText = item.progress !== undefined ? `, ${labels.progress} ${this.buildProgressLabel(item.progress)}` : '';
                return `${item.name}: ${item.value}${targetText}${progressText}`;
              })
              .join('; ');

            lines.push(`- ${title}: ${coverageText || '--'}`);
            return;
          }

          const parts = [`${labels.value} ${metric.value || '--'}`];
          if (metric.target) parts.push(`${labels.target} ${metric.target}`);
          if (metric.unitKey) parts.push(`${labels.unit} ${this.$t(metric.unitKey)}`);
          if (metric.progress !== undefined) parts.push(`${labels.progress} ${this.buildProgressLabel(metric.progress)}`);
          if (metric.footerKey || metric.footerValue) {
            const footerLabel = metric.footerKey ? this.$t(metric.footerKey) : '';
            parts.push(`${footerLabel} ${metric.footerValue || ''}`.trim());
          }

          lines.push(`- ${title}: ${parts.join(', ')}`);
        });
      });

      return lines;
    },
    buildKpiCopilotPrompt() {
      const lines = this.buildKpiResultLines().join('\n');
      const isEnglish = /^en/i.test(this.$i18n && this.$i18n.locale ? this.$i18n.locale : '');

      if (isEnglish) {
        return [
          'Please analyze the following sales KPI results and provide more KPI improvement suggestions. Focus on underperforming or risky metrics, explain likely causes, and recommend specific next actions and useful follow-up questions.',
          '',
          `Role: ${this.roleLabel}`,
          `Period: ${this.dashboard.period}`,
          `Statistics as of: ${this.dashboard.updatedAt}`,
          '',
          'KPI results:',
          lines
        ].join('\n');
      }

      return [
        '请基于以下销售KPI结果，给我更多KPI改善建议。请优先关注未达标或存在风险的指标，说明可能原因，并给出具体下一步行动和可继续追问的问题。',
        '',
        `角色：${this.roleLabel}`,
        `周期：${this.dashboard.period}`,
        `统计截止：${this.dashboard.updatedAt}`,
        '',
        'KPI结果：',
        lines
      ].join('\n');
    },
    openKpiCopilot() {
      this.$root.$emit('open-ai-chat', this.buildKpiCopilotPrompt(), true);
    }
  }
};
</script>

<style lang="scss" scoped>
.kpi-analysis {
  height: 100%;
  background: linear-gradient(180deg, #f8fbff 0%, #f4f7fb 38%, #f8fafc 100%);
  display: flex;
  flex-direction: column;
}

.header {
  height: 52px;
  background: rgba(255, 255, 255, 0.96);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  flex-shrink: 0;

  .title {
    flex: 1;
    overflow: hidden;
    font-size: 17px;
    font-weight: 700;
    color: #111827;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .left,
  .right {
    width: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.content {
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 24px;
}

.drill-down-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.summary-card {
  background: rgba(255, 255, 255, 0.94);
  border-radius: 22px;
  padding: 18px;
  margin-bottom: 18px;
  box-shadow: 0 18px 40px rgba(148, 163, 184, 0.16);

  .summary-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
  }

  .summary-title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
  }

  .updated-at {
    flex: 0 0 auto;
    color: #64748b;
    font-size: 11px;
    text-align: right;
    white-space: nowrap;
  }

  .summary-desc {
    font-size: 12px;
    line-height: 1.55;
    color: #64748b;
  }
}

.kpi-loading-state {
  display: flex;
  flex-direction: column;
}

.kpi-loading-indicator {
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.skeleton-summary-card,
.skeleton-section-card,
.skeleton-metric-card {
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 18px 40px rgba(148, 163, 184, 0.12);
}

.skeleton-summary-card {
  border-radius: 22px;
  padding: 18px;
  margin-bottom: 18px;
}

.skeleton-section-card {
  border-radius: 22px;
  padding: 18px 14px 14px;
  margin-bottom: 16px;
}

.skeleton-section-header {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}

.skeleton-badge {
  width: 28px;
  height: 28px;
  border-radius: 10px;
  margin-right: 10px;
  flex: 0 0 auto;
  background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: kpi-skeleton-shimmer 1.25s ease-in-out infinite;
}

.skeleton-metrics {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(12, minmax(0, 1fr));
}

.skeleton-metric-card {
  grid-column: span 6;
  border-radius: 12px;
  padding: 16px 12px 12px;
  min-height: 116px;
  display: flex;
  flex-direction: column;
}

.skeleton-metric-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
}

.skeleton-line,
.skeleton-pill,
.skeleton-track {
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: kpi-skeleton-shimmer 1.25s ease-in-out infinite;
}

.skeleton-title-line {
  width: 58%;
  height: 16px;
  margin-bottom: 12px;
}

.skeleton-desc-line {
  width: 100%;
  height: 10px;
  margin-bottom: 8px;

  &.short {
    width: 72%;
    margin-bottom: 0;
  }
}

.skeleton-section-title {
  width: 38%;
  height: 15px;
}

.skeleton-metric-title {
  width: 62%;
  height: 12px;
}

.skeleton-pill {
  width: 34px;
  height: 16px;
  flex: 0 0 auto;
}

.skeleton-value-line {
  width: 46%;
  height: 22px;
  margin-bottom: 14px;
}

.skeleton-foot-line {
  width: 68%;
  height: 10px;
  margin-bottom: 12px;
}

.skeleton-track {
  height: 6px;
  width: 100%;
  margin-top: auto;
}

@keyframes kpi-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0 50%;
  }
}

.section-card {
  background: rgba(255, 255, 255, 0.94);
  border-radius: 22px;
  padding: 18px 14px 14px;
  margin-bottom: 16px;
  box-shadow: 0 18px 40px rgba(148, 163, 184, 0.14);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 14px;

  .section-title-wrap {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .section-badge {
    width: 28px;
    height: 28px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    margin-right: 10px;
  }

  .section-title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
  }
}

.section-metrics {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  align-items: stretch;

  &.metrics-1 .metric-card {
    grid-column: span 12;
  }

  &.metrics-2 .metric-card {
    grid-column: span 6;
  }

  &.metrics-3 .metric-card {
    grid-column: span 4;
  }

  &.metrics-4 .metric-card {
    grid-column: span 6;
  }
}

.metric-card {
  background: linear-gradient(180deg, var(--section-soft) 0%, rgba(255, 255, 255, 0.98) 76%);
  border-radius: 12px;
  padding: 16px 12px 12px;
  min-width: 0;
  display: flex;
  flex-direction: column;

  &.metric-drillable {
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease;

    &:active {
      transform: scale(0.985);
    }

    &:focus-visible {
      outline: 2px solid var(--section-accent);
      outline-offset: 2px;
    }
  }

  .metric-title {
    font-size: 12px;
    line-height: 1.45;
    font-weight: 600;
    color: #1f2937;
    word-break: normal;
  }

  .metric-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
  }

  .metric-header-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 4px;
  }

  .metric-period {
    flex: 0 0 auto;
    padding: 2px 6px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.72);
    color: var(--section-accent);
    font-size: 10px;
    line-height: 1.4;
    font-weight: 700;
    white-space: nowrap;
  }

  .metric-drill-arrow {
    color: var(--section-accent);
  }

  .metric-value-row {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 10px;
  }

  .metric-value {
    font-size: 22px;
    line-height: 1;
    font-weight: 700;
    color: var(--section-accent);
  }

  .metric-target,
  .metric-unit {
    font-size: 12px;
    color: #475569;
    font-weight: 600;
  }

  .metric-footer {
    font-size: 12px;
    color: #64748b;
    margin-bottom: 10px;
  }

  .progress-track {
    height: 6px;
    background: rgba(226, 232, 240, 0.72);
    border-radius: 999px;
    overflow: hidden;
    margin-top: auto;
  }

  .progress-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.5s ease;
  }

  .threshold-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    color: var(--section-accent);
    margin-top: auto;
  }
}

.metric-coverage {
  .coverage-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: auto;
  }

  .coverage-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .coverage-value-item {
    display: flex;
    align-items: baseline;
    justify-content: flex-start;
    gap: 4px;
    min-width: 0;
  }

  .coverage-name {
    font-size: 12px;
    color: #334155;
    font-weight: 700;
  }

  .coverage-value {
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
  }

  .coverage-target {
    font-size: 10px;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
  }

  .coverage-track {
    height: 6px;
    background: rgba(226, 232, 240, 0.72);
    border-radius: 999px;
    overflow: hidden;
    margin-top: 0;
  }

  .coverage-fill {
    height: 100%;
    border-radius: 999px;
  }
}

.kpi-copilot-entry {
  width: 100%;
  border: 1.5px solid transparent;
  border-radius: 32px;
  padding: 10px 12px;
  background: linear-gradient(#fff, #fff) padding-box, linear-gradient(165deg, #4d50e9 0%, #b84fd7 50%, #aa341b 100%) border-box;
  box-shadow: 0 8px 18px rgba(77, 80, 233, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 9px;
  text-align: left;
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:active {
    transform: scale(0.99);
    box-shadow: 0 4px 12px rgba(170, 52, 27, 0.1);
  }
}

.kpi-copilot-title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  background: linear-gradient(165deg, #4d50e9 0%, #b84fd7 50%, #aa341b 100%);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.kpi-copilot-desc {
  margin: 8px 2px 0;
  font-size: 12px;
  line-height: 1.45;
  color: #64748b;
}

.kpi-copilot-arrow {
  flex: 0 0 auto;
  margin-left: auto;
  color: #b84fd7;
}

@media (max-width: 375px) {
  .summary-card {
    .summary-title-row {
      align-items: flex-start;
      flex-direction: column;
      gap: 4px;
    }

    .updated-at {
      font-size: 11px;
      text-align: left;
    }
  }

  .section-metrics {
    &.metrics-3 .metric-card,
    &.metrics-4 .metric-card {
      grid-column: span 6;
    }
  }

}
</style>
